// src/tabs/calendar/TableauView.jsx — Phase 2.G.6 (ADR-033)
// Vue tableau Gantt-style (inspirée SuperHote/Smoobu).
//   - 1 ligne = 1 logement (col gauche sticky)
//   - 1 colonne = 1 jour du mois courant (header sticky top)
//   - Résas en barres horizontales étendues sur leur durée (start_date inclus → end_date exclus)
//   - Click barre → ouvre ResaV2Modal via onSelectResa
//
// Indicateurs (💰 🔒 ⚠️ 🔥) + couleurs source/temporalité + today vertical line :
//   livrés en commit 2 de cette PR.

import { useMemo, memo, useCallback, useRef, useEffect } from "react"
import { Lock, AlertTriangle, Flame, Pin, CloudOff, RefreshCw, Check } from "lucide-react"
import { DS } from "../../lib/ui"
import { SOURCE_BG, SOURCE_BG_FALLBACK, PRICING_TEXT_STYLE } from "./_source-colors"
// Verrou sélectif — libellé unique du repère 📌 (texte exact validé Dimitri).
import { PRICING_LOCK_LABEL } from "../../lib/pricing-display"
import { findBlockForDate, blocksToMonthSpans } from "./_block-helpers"
// v3 ÉTAPE 1 (read-path Beds24) — barres « Bloqué sur Beds24 » : diagnostic
// TEMPORAIRE rendant visibles les dates num_avail=0 non représentées dans l'app.
import { beds24BlockedSpans, BEDS24_ORIGIN_VISUAL, BEDS24_REOPENED_VISUAL, isSpanReopened } from "./_beds24-origin"
import { formatMinStayCellShort } from "./_min-stay-helpers"  // PR #167c (ADR-101 D-8) + amend C18 (chiffre seul)
import { getBlockVisualState } from "./_block-visual"  // PR #169a v1.4 (ADR-104)
// Re-mapping conditions : dérivation paiement/caution source-aware. Les
// timestamps payment_received_at / caution_received_at sont VIDES en prod →
// l'icône 💰 ne s'affichait jamais (Airbnb payé inclus). On dérive l'état réel
// depuis source + statuts métier (mirror lib/payment-derivation.js).
import { sejourPaye, cautionRecue } from "../../lib/payment-derivation"
import { filterActiveResas } from "../../lib/calendar-active-resas"

function isSameDay(a, b) {
  return a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate()
}

function isWeekend(d) {
  const dow = d.getDay()
  return dow === 0 || dow === 6
}

const DAY_LETTERS = ['D','L','M','M','J','V','S']  // index = dayOfWeek (0=dim)

// Indicateurs par résa (Phase 2.G.6) — logique source-spécifique :
//   airbnb            : 💰 si paiement marqué (optionnel, AirCover natif), 🔥 si on-site
//   booking/direct    : 💰 si payé, 🔒 si caution reçue, ⚠️ si action requise
//                       (paiement à relancer J-3 OU caution à demander J-1), 🔥 si on-site
function getIndicators(resa, today) {
  const icons = []
  const isPaid = sejourPaye(resa)
  const hasCaution = cautionRecue(resa)
  const startDate = new Date(resa.start_date); startDate.setHours(0, 0, 0, 0)
  const endDate = new Date(resa.end_date);     endDate.setHours(0, 0, 0, 0)
  const daysUntilStart = Math.floor((startDate - today) / (1000 * 60 * 60 * 24))
  const isOnSite = startDate <= today && today < endDate
  const requiresCaution = ['booking', 'direct'].includes(resa.source)

  if (isPaid) icons.push('💰')
  if (requiresCaution && hasCaution) icons.push('🔒')

  if (requiresCaution) {
    if (!isPaid && daysUntilStart < 4 && daysUntilStart >= 0) {
      icons.push('⚠️')   // paiement à relancer (J-3 ou moins)
    } else if (isPaid && !hasCaution && daysUntilStart < 2 && daysUntilStart >= 0) {
      icons.push('⚠️')   // caution à demander (J-1)
    }
  }

  if (isOnSite) icons.push('🔥')
  return icons
}

function indicatorTooltip(icons, resa) {
  if (icons.length === 0) return ''
  const parts = []
  if (icons.includes('💰')) parts.push('💰 paiement reçu')
  if (icons.includes('🔒')) parts.push('🔒 caution reçue')
  if (icons.includes('⚠️')) {
    if (!sejourPaye(resa)) parts.push('⚠️ paiement à relancer (J-3 ou moins)')
    else if (!cautionRecue(resa)) parts.push('⚠️ caution à demander (J-1)')
  }
  if (icons.includes('🔥')) parts.push('🔥 voyageur sur place')
  return parts.join(' · ')
}

// Couleur barre = SOURCE uniquement (DA §8 bis) ; la temporalité ne joue que
// sur l'estompage du passé (jamais sur la teinte de source).
function getBarStyle(resa, today) {
  const baseBg = SOURCE_BG[resa.source] || SOURCE_BG_FALLBACK
  const endDate = new Date(resa.end_date); endDate.setHours(0, 0, 0, 0)
  const isPast = endDate <= today
  // Past : barre estompée (séjour terminé). L'état « sur place » est désormais
  // porté par le marqueur fort (carré-icône), plus par une bordure de couleur.
  return {
    background: baseBg,
    opacity: isPast ? 0.5 : 1,
    filter: isPast ? 'grayscale(35%)' : 'none',
    border: 'none',
  }
}

// DA L1 #3/#4 — traduit les états DÉRIVÉS (icons emoji de getIndicators,
// INTACTS) en UN marqueur visuel sur la barre. La dérivation n'est pas touchée :
// on ne change QUE le rendu.
//   - SEULS les marqueurs d'ALERTE (à traiter) sont rendus. Le point « sous
//     contrôle », l'icône « payé » (💰) et « caution reçue » (🔒) sont RETIRÉS
//     de l'affichage (redondants : une résa affichée est forcément payée).
//   - Alertes conservées, rhabillées en pastille lucide à couleur sémantique :
//       · voyageur sur place (🔥)         → Flame, ton warning
//       · paiement à relancer (⚠️ !payé)  → AlertTriangle, ton danger
//       · caution à demander (⚠️ payé)    → AlertTriangle, ton warning
//   - Aucune alerte → null (la barre ne porte plus que le nom du voyageur).
// Précédence d'icône préservée (onSite avant action, cf. ex-getStateMarkers).
function getAlertMarker(icons, resa) {
  const onSite = icons.includes('🔥')
  const action = icons.includes('⚠️')
  if (onSite) return { Icon: Flame, tone: 'warning', label: 'voyageur sur place' }
  if (action) {
    return sejourPaye(resa)
      ? { Icon: AlertTriangle, tone: 'warning', label: 'caution à demander' }
      : { Icon: AlertTriangle, tone: 'danger', label: 'paiement à relancer' }
  }
  return null
}

function toISODate(d) {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`
}

// Phase 6.B PR5A (fix N-QA-1) — useDailyPricing hissé au parent CalendarPanel
// pour partager le cache avec modals (Create/Details). Cette vue reçoit
// pricingMap/modesByLogement/blocksByLogement/invalidate en props.
// findBlockForDate mutualisé dans _block-helpers (N-QA-6).
// (MensuelView retirée PR #169d — partage hook avec TodayView via blocksByLogement.)
export default function TableauView({
  currentMonth, today, allLogs, filtered, onSelectResa, onCellClick,
  pricingMap, modesByLogement, blocksByLogement, invalidate,
  minStayDefaultsByLogement,  // PR #167c (ADR-101 D-8 + MPRcv2-2)
  showMinStay = false,
  // R2-UI (ADR-256) — clic sur une barre hachurée « Bloqué sur Beds24 ».
  // Callback remonté au parent (CalendarPanel) avec le span à réconcilier :
  // { logementId, logementLabel, startISO, endISO } (endISO = dernière date
  // bloquée INCLUSE). Absent → barres non cliquables (rétrocompat tests isolés).
  onBeds24Divergence = null,
  // Micro-UX M14 — segments dont la réouverture Beds24 vient d'être envoyée
  // (état optimiste session, [{logementId, start, end}] bornes ISO incluses).
  // Un span hachuré chevauchant un de ces segments est rendu gris neutre
  // « Réouverture envoyée ✓ », non cliquable, en attendant le refresh miroir.
  beds24ReopenedSegments = null,
  // LOT 5 — Map<logement_id, Map<dateISO, status>> des dates couvertes par un
  // push outbox NON confirmé (pending/failed/syncing). Sert le ⚠️ par case.
  // Map vide par défaut (cas nominal : outbox vide → aucun ⚠️).
  outboxPendingByLogement = null,
  // Fantômes calendrier (décision Dimitri 2026-07-20) — Map<logement_id,
  // Array<{id, start_date, end_date, guest_first_name, guest_last_name,
  // cancelled_at}>> des blocs ANNULÉS (demande directe 'cancelled', fenêtre
  // 30 j servie par le payload batch). null / absent → aucun fantôme rendu
  // (c'est ainsi que l'interrupteur Légende « Afficher les fantômes ci-dessus »
  // masque :
  // CalendarPanel passe null quand décoché — filtre 100 % client).
  cancelledBlocksByLogement = null,
}) {
  // PR #168 D-3 + D-8 — Index résas par logement par dateISO.
  // - D-3 : sert à masquer le prix (bug "34") sur cellules sous résa.
  // - D-8 (Q-4 ajusté) : sert à ouvrir ResaV2Modal au clic plutôt que la modal prix.
  // Convention chambre d'hôtel : start_date inclus → end_date exclus.
  // Si plusieurs résas même jour (check-in jour de check-out), on prend la
  // PREMIÈRE par ordre `filtered` (déjà trié start_date ASC côté CalendarPanel).
  // Fix calendrier — une résa annulée (status cancelled/canceled) ne doit plus
  // occuper sa case ni afficher sa barre (couche affichage uniquement : les
  // résas restent en base, l'historique vit dans la Liste). On dérive ici la
  // liste des résas ACTIVES, consommée par resaIdByLogementByDate ET bars.
  // Le filtre inline a été remplacé par `filterActiveResas` (src/lib/
  // calendar-active-resas.js) pour que le compteur du pied de page de
  // CalendarPanel compte EXACTEMENT ce que cette vue dessine.
  const activeResas = useMemo(() => filterActiveResas(filtered), [filtered])
  const resaIdByLogementByDate = useMemo(() => {
    const out = new Map()
    for (const r of activeResas) {
      if (!r?.logement_id || !r?.start_date || !r?.end_date) continue
      let inner = out.get(r.logement_id)
      if (!inner) { inner = new Map(); out.set(r.logement_id, inner) }
      const s = new Date(r.start_date); s.setHours(0, 0, 0, 0)
      const e = new Date(r.end_date);   e.setHours(0, 0, 0, 0)
      const cur = new Date(s)
      while (cur < e) {
        const key = toISODate(cur)
        if (!inner.has(key)) inner.set(key, r.id) // garde la première (start_date ASC)
        cur.setDate(cur.getDate() + 1)
      }
    }
    return out
  }, [activeResas])
  // Liste des jours du mois courant (pas de débordement sur mois adjacents — la vue est strictement le mois)
  const monthDays = useMemo(() => {
    const first = new Date(currentMonth)
    const last = new Date(currentMonth); last.setMonth(last.getMonth() + 1); last.setDate(0)
    const days = []
    const cur = new Date(first)
    while (cur <= last) {
      days.push(new Date(cur))
      cur.setDate(cur.getDate() + 1)
    }
    return days
  }, [currentMonth])
  const N = monthDays.length

  // Phase 6 PR2 (ADR-093) — pricing journalier batch fourni par CalendarPanel
  // parent (fix N-QA-1) : pricingMap + modesByLogement + blocksByLogement +
  // invalidate. Cache mutualisé avec modals → globalInvalidate()
  // après Create/Release rafraîchit immédiatement les cellules grisées.

  // Phase 6 PR3 (ADR-094) — handler clic cellule. Dispatch selon mode :
  //   - PR #168 D-8 : cellule SOUS RÉSA → ouvre ResaV2Modal (priorité absolue)
  //   - block présent → ouvre modal Details (Phase 6.B PR5A)
  //   - manual  → ouvre modal édition (caller responsable)
  //   - pricelabs → ouvre tooltip/lien externe (caller responsable)
  //   - null    → toast "configurer ce logement"
  const handleCellClick = useCallback(
    (logementId, dateISO, block) => {
      // PR #168 D-8 — priorité : si la cellule est sous une résa, on ouvre la fiche résa.
      const resaId = resaIdByLogementByDate.get(logementId)?.get(dateISO)
      if (resaId && onSelectResa) {
        onSelectResa(resaId)
        return
      }
      if (!onCellClick) return
      const mode = modesByLogement.get(logementId) ?? null
      onCellClick({ logementId, dateISO, mode, invalidate, block })
    },
    [onCellClick, modesByLogement, invalidate, resaIdByLogementByDate, onSelectResa],
  )

  // Index logement → ligne (0-based)
  const logementRow = useMemo(() => {
    const m = new Map()
    allLogs.forEach((l, i) => m.set(l.id, i))
    return m
  }, [allLogs])

  // Today column (0-based dans monthDays) si today appartient au mois affiché
  const todayCol = useMemo(() => {
    if (today.getMonth() !== currentMonth.getMonth() || today.getFullYear() !== currentMonth.getFullYear()) return null
    return today.getDate() - 1
  }, [today, currentMonth])

  // Phase 6 PR4 commit 10 — UX-2 : scroll auto vers colonne aujourd'hui au mount.
  // Pattern useRef + flag didInitialScroll : ne se re-déclenche pas si admin
  // navigue mois précédent puis revient (anti-réinitialisation).
  const todayHeaderRef = useRef(null)
  const didInitialScrollRef = useRef(false)
  useEffect(() => {
    if (didInitialScrollRef.current) return
    if (!todayHeaderRef.current) return
    didInitialScrollRef.current = true
    todayHeaderRef.current.scrollIntoView({
      behavior: 'auto',     // instantané (pas smooth) au load
      inline: 'center',     // centré horizontalement
      block: 'nearest',     // pas de scroll vertical
    })
  }, [])

  // Pré-calcul barres : { resa, logementIdx, startCol, endCol }
  const bars = useMemo(() => {
    const out = []
    for (const r of activeResas) {
      const li = logementRow.get(r.logement_id)
      if (li == null) continue  // logement non trouvé dans cfg

      const s = new Date(r.start_date); s.setHours(0, 0, 0, 0)
      const e = new Date(r.end_date);   e.setHours(0, 0, 0, 0)
      const monthFirst = new Date(currentMonth)
      const monthLast = new Date(currentMonth); monthLast.setMonth(monthLast.getMonth() + 1); monthLast.setDate(0)

      // Convention chambre d'hôtel : barre de start_date inclus → end_date exclus.
      // → endLastIncluded = end_date - 1 jour.
      const endLastIncluded = new Date(e); endLastIncluded.setDate(endLastIncluded.getDate() - 1)

      // Si la résa ne touche pas le mois affiché, skip
      if (endLastIncluded < monthFirst || s > monthLast) continue

      const startCol = s < monthFirst ? 0 : (s.getDate() - 1)
      const endCol = endLastIncluded > monthLast ? (N - 1) : (endLastIncluded.getDate() - 1)
      if (endCol < startCol) continue

      out.push({ resa: r, logementIdx: li, startCol, endCol })
    }
    return out
  }, [activeResas, logementRow, currentMonth, N])

  // BUG1 — Pré-calcul barres BLOCS : { block, logementIdx, startCol, endCol }.
  // Une barre continue par block (cf. blocksToMonthSpans) remplace l'illusion
  // « plusieurs voyageurs » du rendu per-cell. Calque sur `bars` (résas) mais
  // source = pricing_blocks (prop `blocksByLogement`).
  const blockBars = useMemo(() => {
    const out = []
    for (const l of allLogs) {
      const li = logementRow.get(l.id)
      if (li == null) continue
      const blocks = blocksByLogement?.get(l.id)
      if (!blocks || blocks.length === 0) continue
      for (const span of blocksToMonthSpans(blocks, monthDays)) {
        out.push({ block: span.block, logementIdx: li, startCol: span.startCol, endCol: span.endCol })
      }
    }
    return out
  }, [allLogs, logementRow, blocksByLogement, monthDays])

  // Fantômes calendrier — barres « résa annulée » : segments des blocs annulés
  // sur le mois affiché. Réutilise blocksToMonthSpans (même convention
  // end_date EXCLUSIVE / clamp mois que les blocs actifs). NON-BLOQUANT par
  // construction : ces blocs ne participent NI à resaIdByLogementByDate, NI à
  // findBlockForDate, NI à beds24BlockedSpans — les dates restent vendables.
  const ghostBars = useMemo(() => {
    if (!cancelledBlocksByLogement) return []
    const out = []
    for (const l of allLogs) {
      const li = logementRow.get(l.id)
      if (li == null) continue
      const cancelled = cancelledBlocksByLogement.get(l.id)
      if (!cancelled || cancelled.length === 0) continue
      for (const span of blocksToMonthSpans(cancelled, monthDays)) {
        out.push({ block: span.block, logementIdx: li, startCol: span.startCol, endCol: span.endCol })
      }
    }
    return out
  }, [allLogs, logementRow, cancelledBlocksByLogement, monthDays])

  // v3 ÉTAPE 1 (read-path Beds24) — barres « Bloqué sur Beds24 ».
  // Diagnostic TEMPORAIRE, LECTURE SEULE : segments contigus de dates
  // num_avail=0 (source Beds24, via pricingMap) NON représentées dans l'app
  // (ni pricing_block FBZ, ni réservation active). Rend visible où les 2 stores
  // divergent. N'ajoute AUCUN clic (pointerEvents:none → le clic passe à la
  // cellule dessous), ne crée aucun block, ne pousse rien vers Beds24.
  const beds24OnlyBars = useMemo(() => {
    const out = []
    for (const l of allLogs) {
      const li = logementRow.get(l.id)
      if (li == null) continue
      const spans = beds24BlockedSpans({
        pricingForLogement: pricingMap?.get(l.id),
        blocksForLogement: blocksByLogement?.get(l.id) || null,
        resaIdByDate: resaIdByLogementByDate.get(l.id) || null,
        monthDays,
      })
      for (const s of spans) {
        // R2-UI (ADR-256) — on porte les DATES du span pour l'action de
        // réconciliation. endISO = dernière date bloquée INCLUSE (le serveur
        // convertit en exclusif). Le span client n'a qu'à être CONTENU dans le
        // segment réel : le serveur recompute le segment contigu.
        const startISO = toISODate(monthDays[s.startCol])
        const endISO = toISODate(monthDays[s.endCol])
        out.push({
          logementIdx: li,
          startCol: s.startCol,
          endCol: s.endCol,
          logementId: l.id,
          logementLabel: l.nom,
          startISO,
          endISO,
          // Micro-UX M14 — réouverture déjà envoyée sur ce segment (optimiste).
          reopened: isSpanReopened({ logementId: l.id, startISO, endISO }, beds24ReopenedSegments),
        })
      }
    }
    return out
  }, [allLogs, logementRow, pricingMap, blocksByLogement, resaIdByLogementByDate, monthDays, beds24ReopenedSegments])

  // Grid template columns : 220px (col logement) + N × minmax(38px, 1fr)
  const gridTemplateColumns = `220px repeat(${N}, minmax(38px, 1fr))`

  // PR #167c amend C5 — 2 rows par logement si showMinStay (sub-row 2 = min nuits).
  // Le sous-row 2 est ~24px (compact) tandis que le sous-row 1 est ~50px (existant).
  // Pour gérer le mix, on n'utilise plus gridAutoRows uniforme — on définit
  // gridTemplateRows explicite : header 50px + (price 50px + minStay 24px)* OU
  // (price 50px)* selon showMinStay. La ligne header reste row 1 sticky.
  //
  // Calcul indices : priceGridRow(li) = 2 + li * rowsPerLog ; minStayGridRow(li)
  // = priceGridRow(li) + 1 si showMinStay. Logique encapsulée dans helpers
  // ci-dessous pour clarté.
  const rowsPerLog = showMinStay ? 2 : 1
  const gridTemplateRows = showMinStay
    ? `50px ${allLogs.map(() => '50px 24px').join(' ')}`
    : `50px ${allLogs.map(() => '50px').join(' ')}`

  return (
    <div style={{overflow:"auto",maxHeight:"calc(100vh - 240px)",border:"1px solid #e8edf4",borderRadius:8,position:"relative",background:DS.white}}>
      <div style={{display:"grid",gridTemplateColumns,gridTemplateRows,position:"relative"}}>

        {/* Coin sticky top + left (z-index max) */}
        <div style={{
          gridColumn:1,gridRow:1,
          position:"sticky",top:0,left:0,zIndex:3,
          background:DS.white,borderRight:"1px solid #e8edf4",borderBottom:"1px solid #e8edf4",
          padding:"10px 12px",fontSize:11,fontWeight:700,color:DS.navy,
          display:"flex",alignItems:"center",
        }}>
          Logement
        </div>

        {/* Header jours (sticky top).
            DA §8 bis : chiffre du jour en Fraunces, colonne du jour teintée
            corail clair (corailSoft + liséré bas corail). Week-end = gris neutre
            (jamais de bleu-gris générique). */}
        {monthDays.map((d, i) => {
          const isToday = isSameDay(d, today)
          const we = isWeekend(d)
          return (
            <div
              key={'h' + i}
              ref={isToday ? todayHeaderRef : null}
              style={{
                gridColumn:i + 2,gridRow:1,
                position:"sticky",top:0,zIndex:2,
                background:isToday ? DS.corailSoft : (we ? "#f4f4f5" : DS.white),
                borderBottom:isToday ? `2px solid ${DS.corail}` : "1px solid #e8edf4",
                borderRight:"1px solid #f0f4f8",
                padding:"4px 2px",textAlign:"center",fontSize:11,
                display:"flex",flexDirection:"column",alignItems:"center",justifyContent:"center",
              }}>
              <div style={{fontFamily:DS.fontDisplay,fontSize:14,fontWeight:600,color:isToday ? DS.corailText : DS.marine}}>{d.getDate()}</div>
              <div style={{fontSize:9,color:DS.textMuted}}>{DAY_LETTERS[d.getDay()]}</div>
            </div>
          )
        })}

        {/* Lignes logements
            PR #167c (MPRcv2-2) — extraction au niveau parent : on passe le
            scalaire `minStayDefault` au Row (pas la Map entière) pour préserver
            l'égalité référentielle React.memo et éviter re-render N×31 cells.

            PR #167c amend C5 — `rowsPerLog` et `priceGridRow` calculés par
            parent + passés au Row, qui rend conditionnellement une 2e ligne
            min_stay si showMinStay=true. */}
        {allLogs.map((l, li) => (
          <Row
            key={l.id}
            logement={l}
            rowIdx={li}
            N={N}
            monthDays={monthDays}
            today={today}
            pricingForLogement={pricingMap.get(l.id)}
            modeForLogement={modesByLogement.get(l.id) ?? null}
            blocksForLogement={blocksByLogement?.get(l.id) || null}
            resaIdByDate={resaIdByLogementByDate.get(l.id) || null}
            minStayDefault={minStayDefaultsByLogement?.get(l.id) ?? null}
            outboxPendingForLogement={outboxPendingByLogement?.get(l.id) || null}
            onCellClick={handleCellClick}
            showMinStay={showMinStay}
            priceGridRow={2 + li * rowsPerLog}
          />
        ))}

        {/* Today vertical line (Phase 2.G.6) — covers full grid height.
            DA : liséré corail (couleur d'attention de marque). */}
        {todayCol !== null && (
          <div aria-hidden="true" style={{
            gridColumn:todayCol + 2,
            gridRow:'1 / -1',
            borderLeft:`2px solid ${DS.corail}`,
            pointerEvents:'none',
            zIndex:0,
          }}/>
        )}

        {/* Fantômes calendrier — barres « résa annulée » (décision Dimitri
            2026-07-20). Style DISTINCT de tout l'existant : fond transparent,
            bordure POINTILLÉE grise, nom du voyageur BARRÉ, opacité réduite —
            ni plein (résa active), ni hachuré ciel (Beds24), ni gris plein
            (blocage / réouverture envoyée ✓).
            NON-BLOQUANT — rendu « liseré inférieur » : barre FINE (20px)
            ancrée en BAS de la sub-row prix (alignSelf:end), là où les barres
            résa/bloc (38px) sont ancrées en HAUT →
              · le haut de la cellule reste cliquable (vendre / prix / bloquer) ;
              · une résa active sur les mêmes dates, rendue APRÈS dans le DOM
                (même zIndex 1 → ordre DOM fait foi), passe PAR-DESSUS et ne
                laisse dépasser du fantôme qu'un fin liseré pointillé en bas.
            Clic sur le fantôme → ResaV2Modal kind=pricing_block en lecture
            (le bloc est inactif : la modale affiche le statut CANCELLED et le
            footer « Période libérée le … » — pas de bouton Débloquer). */}
        {ghostBars.map(({ block, logementIdx, startCol, endCol }) => {
          const priceGridRow = 2 + logementIdx * rowsPerLog
          const ghostName = `${block.guest_first_name || '?'}${block.guest_last_name ? ' ' + block.guest_last_name[0].toUpperCase() + '.' : ''}`
          const cancelledDay = block.cancelled_at ? String(block.cancelled_at).slice(0, 10) : null
          return (
            <button key={`ghost-${block.id}`}
              type="button"
              data-testid={`cancelled-ghost-bar-${block.id}`}
              onClick={() => { if (onCellClick) onCellClick({ logementId: block.logement_id, dateISO: block.start_date, mode: modesByLogement.get(block.logement_id) ?? null, invalidate, block }) }}
              title={`Résa directe ANNULÉE — ${block.guest_first_name || ''} ${block.guest_last_name || ''} (${block.start_date} → ${block.end_date})${cancelledDay ? ` · annulée le ${cancelledDay}` : ''} · dates re-vendables`.replace(/\s+\(/, ' (')}
              aria-label={`Résa directe annulée — ${ghostName} (${block.start_date} → ${block.end_date})`}
              className="fbz-press"
              style={{
                gridColumn:`${startCol + 2} / ${endCol + 3}`,
                gridRow:priceGridRow,
                alignSelf:"end",
                margin:"0 2px 3px",height:20,
                borderRadius:6,
                background:"transparent",
                border:"1.5px dashed #9ca3af",
                opacity:0.7,
                padding:"0 8px",fontSize:10,fontWeight:600,color:"#6b7280",
                cursor:"pointer",
                display:"flex",alignItems:"center",
                overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",
                zIndex:1,
                fontFamily:DS.fontBody,textAlign:"left",
              }}>
              <span style={{flex:"1 1 auto",minWidth:0,overflow:"hidden",textOverflow:"ellipsis",textDecoration:"line-through"}}>
                {ghostName}
              </span>
            </button>
          )
        })}

        {/* Barres résas — gridRow ciblé sur la sub-row "prix" (top de chaque
            paire logement quand showMinStay=true). PR #167c amend C5 :
            barre ne déborde JAMAIS sur la sub-row min_stay (24px). */}
        {bars.map(({ resa, logementIdx, startCol, endCol }) => {
          const icons = getIndicators(resa, today)
          const barStyle = getBarStyle(resa, today)
          const alert = getAlertMarker(icons, resa)
          const baseTooltip = `${resa.guest_first_name || '?'} ${resa.guest_last_name || ''} · ${resa.source} · ${resa.total_amount || '—'}€`
          const indicTooltip = indicatorTooltip(icons, resa)
          const fullTooltip = indicTooltip ? `${baseTooltip}\n${indicTooltip}` : baseTooltip
          const priceGridRow = 2 + logementIdx * rowsPerLog
          return (
            <button key={resa.id}
              onClick={() => onSelectResa(resa.id)}
              title={fullTooltip}
              className="fbz-press"
              style={{
                gridColumn:`${startCol + 2} / ${endCol + 3}`,
                gridRow:priceGridRow,
                margin:"5px 2px",height:38,
                borderRadius:7,
                ...barStyle,
                padding:"4px 8px",fontSize:11,fontWeight:600,color:"#fff",
                cursor:"pointer",
                position:"relative",
                display:"flex",alignItems:"center",
                overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",
                boxShadow:"0 1px 3px rgba(0,0,0,0.15)",
                zIndex:1,
                fontFamily:DS.fontBody,textAlign:"left",
              }}>
              {/* DA L1 #2 — nom du voyageur PRIORITAIRE et TOUJOURS visible, y
                  compris barres 1 nuit : placé seul en flux flex (flex:1,
                  minWidth:0) → il occupe toute la largeur de la barre et n'est
                  jamais écrasé par un ornement (le marqueur d'alerte est en
                  position absolue, hors flux, cf. ci-dessous). */}
              <span style={{flex:"1 1 auto",minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>
                {resa.guest_first_name || '?'}{resa.guest_last_name ? ' ' + resa.guest_last_name[0].toUpperCase() + '.' : ''}
              </span>
              {/* DA L1 #4 — marqueur d'ALERTE seul (warning/danger), pastille
                  lucide. position:absolute → ne consomme jamais l'espace flex du
                  nom (garantie #2). Clippé par overflow:hidden de la barre. */}
              {alert && (
                <span aria-hidden="true" style={{
                  position:"absolute",top:"50%",right:3,transform:"translateY(-50%)",
                  flexShrink:0,width:18,height:18,borderRadius:5,
                  background: alert.tone === "danger" ? DS.danger : DS.warning,
                  display:"flex",alignItems:"center",justifyContent:"center",
                  boxShadow:"0 0 0 1.5px rgba(255,255,255,.55)",
                }}>
                  <alert.Icon size={11} strokeWidth={2.4} color="#fff" />
                </span>
              )}
            </button>
          )
        })}

        {/* BUG1 — Barres BLOCS (pricing_blocks) : 1 barre continue par block,
            calquée sur les barres résa. Remplace le rendu per-cell (icône
            répétée par jour) qui donnait l'illusion de plusieurs voyageurs.
            - fond = bgColor du block (violet pastel direct interne | gris simple)
            - icône affichée UNE fois : ternaire inline isDirect ? 👤 : 🔒
              (CORRECTIF PESSIMIST : getBlockVisualState.icon vaut TOUJOURS 🔒)
            - nom : direct → prénom + initiale ; sinon reason || "Bloqué"
            - badge ⟳ si push Beds24 en attente (blocage simple uniquement)
            - clic → handleCellClick LOCAL (route vers ResaV2Modal kind=pricing_block,
              JAMAIS onSelectResa qui ferait un lookup table reservations).
            - title contient "Résa direct interne" pour les directs (contrat E2E). */}
        {blockBars.map(({ block, logementIdx, startCol, endCol }) => {
          const visual = getBlockVisualState(block)
          const isDirect = visual.isDirect
          // Revue adverse #1428 (MAJEUR 1) : une résa DIRECTE dont Beds24 a refusé
          // la fermeture était muette (barre verte sans ⟳) — un voyageur dort
          // dans la chambre pendant que les OTA la vendent. Le badge suit
          // beds24_pushed, direct ou non (ADR-181 : les deux poussent numAvail=0).
          const showRetry = !block.beds24_pushed
          const dateISO = toISODate(monthDays[startCol])  // 1er jour visible du block
          // Décision Dimitri « un blocage est un blocage » : la VUE calendrier
          // affiche TOUJOURS « Blocage » pour un bloc FBZ simple (jamais la
          // reason, qui exposait l'origine ex. « repris depuis Beds24 »). La
          // reason reste consultable dans le détail (ResaV2Modal kind=pricing_block).
          const label = isDirect
            ? `${block.guest_first_name || '?'}${block.guest_last_name ? ' ' + block.guest_last_name[0].toUpperCase() + '.' : ''}`
            : 'Blocage'
          const title = isDirect
            ? `👤 Résa direct interne — ${block.guest_first_name || ''} ${block.guest_last_name || ''} (${block.start_date} → ${block.end_date})`.replace(/\s+\(/, ' (') + (showRetry ? ' · ⟳ fermeture Beds24 non confirmée' : '')
            : `🔒 Blocage (${block.start_date} → ${block.end_date})${showRetry ? ' · ⟳ en attente Beds24' : ''}`
          const priceGridRow = 2 + logementIdx * rowsPerLog
          // DA L1 #5 — forme de barre IDENTIQUE aux barres résa (mêmes coins,
          // hauteur, marge, padding, ombre) ; SEULE la couleur encode la nature :
          //   · résa direct interne = vert srcDirect (texte blanc) — looks like
          //     une vraie résa (elle EN est une), plus de point « sous contrôle ».
          //   · blocage simple = gris srcBloque (texte marine + cadenas d'identité).
          // Le hachuré 45° est SUPPRIMÉ (c'était la « forme différente » =
          // incohérence visuelle) : le blocage reste distinct par sa COULEUR.
          const blockTextColor = isDirect ? "#fff" : DS.marineDark
          return (
            <button key={`block-${block.id}`}
              data-testid={`block-bar-${block.id}`}
              onClick={() => { if (onCellClick) onCellClick({ logementId: block.logement_id, dateISO, mode: modesByLogement.get(block.logement_id) ?? null, invalidate, block }) }}
              title={title}
              className="fbz-press"
              style={{
                gridColumn:`${startCol + 2} / ${endCol + 3}`,
                gridRow:priceGridRow,
                margin:"5px 2px",height:38,
                borderRadius:7,
                background:visual.bgColor,
                padding:"4px 8px",fontSize:11,fontWeight:600,color:blockTextColor,
                cursor:"pointer",
                display:"flex",alignItems:"center",gap:4,
                overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",
                boxShadow:"0 1px 3px rgba(0,0,0,0.15)",
                zIndex:1,
                fontFamily:DS.fontBody,textAlign:"left",
                border:"none",
              }}>
              {/* Blocage simple : cadenas d'identité (le direct n'en a pas — il
                  est rendu comme une résa, nom prioritaire). */}
              {!isDirect && (
                <span aria-hidden="true" style={{flexShrink:0,display:"flex",alignItems:"center"}}>
                  <Lock size={13} strokeWidth={2} color={DS.marineDark} />
                </span>
              )}
              <span style={{flex:"1 1 auto",minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>
                {label}
              </span>
              {showRetry && (
                <span aria-label="en attente Beds24" style={{flexShrink:0,display:"flex",alignItems:"center"}}>
                  <RefreshCw size={12} strokeWidth={2.2} color={DS.warningText} />
                </span>
              )}
            </button>
          )
        })}

        {/* R2-UI (ADR-256) — barres « Bloqué sur Beds24 » CLIQUABLES.
            Trame hachurée ciel VISUELLEMENT DISTINCTE des barres résa/bloc FBZ
            (pleines) : signale un blocage présent côté Beds24 mais non
            représenté dans l'app. Le clic ouvre la modale « Divergence Beds24 »
            (reprendre dans l'app / rouvrir sur Beds24) — il n'atteint PLUS la
            cellule dessous (le bouton capture le clic, c'est voulu). Si aucun
            handler n'est fourni (tests isolés), la barre reste non interactive. */}
        {beds24OnlyBars.map(({ logementIdx, startCol, endCol, logementId, logementLabel, startISO, endISO, reopened }, idx) => {
          const priceGridRow = 2 + logementIdx * rowsPerLog
          // Micro-UX M14 — segment dont la réouverture vient d'être envoyée :
          // gris neutre « Réouverture envoyée ✓ », non cliquable (évite un
          // double push pendant que le miroir num_avail est encore stale).
          const clickable = !!onBeds24Divergence && !reopened
          const visual = reopened ? BEDS24_REOPENED_VISUAL : BEDS24_ORIGIN_VISUAL
          return (
            <button
              key={`beds24only-${logementIdx}-${startCol}-${endCol}-${idx}`}
              type="button"
              data-testid={`beds24-origin-bar-${logementIdx}-${startCol}`}
              data-reopened={reopened ? "true" : undefined}
              aria-label={reopened
                ? `Réouverture envoyée à Beds24 — ${logementLabel || logementId} ${startISO}→${endISO}. Le calendrier se mettra à jour à la prochaine synchro.`
                : `Bloqué sur Beds24 (non représenté dans l'app) — ${logementLabel || logementId} ${startISO}→${endISO}. Cliquer pour réconcilier.`}
              title={reopened
                ? "Réouverture envoyée à Beds24 — le calendrier se mettra à jour à la prochaine synchro (webhook / cron)."
                : "Bloqué sur Beds24 — cette période est indisponible côté Beds24 mais n'est pas encore représentée dans l'app. Cliquer pour la reprendre dans l'app ou la rouvrir sur Beds24."}
              disabled={!clickable}
              onClick={clickable
                ? () => onBeds24Divergence({ logementId, logementLabel, startISO, endISO })
                : undefined}
              className={clickable ? "fbz-press" : undefined}
              style={{
                gridColumn:`${startCol + 2} / ${endCol + 3}`,
                gridRow:priceGridRow,
                margin:"5px 2px",height:38,
                borderRadius:7,
                background:visual.background,
                border:visual.border,
                padding:"4px 8px",fontSize:11,fontWeight:600,
                color:visual.textColor,
                display:"flex",alignItems:"center",gap:4,
                overflow:"hidden",whiteSpace:"nowrap",textOverflow:"ellipsis",
                zIndex:1,
                fontFamily:DS.fontBody,textAlign:"left",
                cursor:clickable ? "pointer" : "default",
              }}>
              <span aria-hidden="true" style={{flexShrink:0,display:"flex",alignItems:"center"}}>
                {reopened
                  ? <Check size={13} strokeWidth={2.4} color={visual.textColor} />
                  : <CloudOff size={13} strokeWidth={2} color={visual.textColor} />}
              </span>
              <span style={{flex:"1 1 auto",minWidth:0,overflow:"hidden",textOverflow:"ellipsis"}}>
                {visual.label}
              </span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// Une ligne logement = col 1 sticky + N cells vides (background week-end / today plus tard commit 2)
//
// Phase 6 PR2 (ADR-093) :
//   - injection prix journaliers en enfant cell direct (BPRv3-1) avec flex-center + pointerEvents:none
//   - React.memo avec comparateur custom (BPRv3-4) — pricingForLogement référence stable
//     via cacheRef du hook useDailyPricing → re-render évité si contenu inchangé.
//
// PR #167c amend C5 — quand showMinStay=true, le Row rend 2 sub-rows (prix +
// min nuits). La col 1 sticky du logement s'étend sur les 2 sub-rows
// (`gridRow: priceGridRow / span 2`). Sous-row min stay est 24px, compact.
// Bars résa restent sur la sub-row prix uniquement (gridRow=priceGridRow).
const Row = memo(function Row({
  logement, rowIdx, N, monthDays, today, pricingForLogement, modeForLogement, blocksForLogement, onCellClick,
  resaIdByDate = null,    // PR #168 D-3 — Map<dateISO, resaId> pour le logement (guard "34" + clic résa)
  minStayDefault = null,  // PR #167c (MPRcv2-2) — scalaire, pas la Map entière
  outboxPendingForLogement = null,  // LOT 5 — Map<dateISO, status> push non confirmés
  showMinStay = false,
  priceGridRow,           // PR #167c amend C5 — index gridRow de la sub-row prix (calculé parent)
}) {
  const showPriceForLogement = modeForLogement != null  // pricing_mode défini (pricelabs OU manual)
  const isClickable = !!onCellClick
  // PR #167c amend C5 — index gridRow de la sub-row min_stay (juste après la sub-row prix).
  const minStayGridRow = priceGridRow + 1
  return (
    <>
      {/* Col 1 sticky left — sub-row 1 PRIX : nom du logement + propriétaire.
          z-index 2 (au-dessus des barres résas z-index 1) pour rester visible
          pendant le scroll horizontal. Corner cell reste à 3.
          PR #167c amend C18 : NE PLUS span 2 rows. Sub-row 2 a sa propre col 1
          avec label "Nb nuits min" (cf. div suivant). */}
      <div style={{
        gridColumn:1,
        gridRow: priceGridRow,
        position:"sticky",left:0,zIndex:2,
        background:DS.white,borderRight:"1px solid #e8edf4",
        // PR #167c amend C18 : pas de border-bottom si la sub-row min_stay
        // existe dessous (la séparation visuelle vient de la sub-row 2).
        borderBottom: showMinStay ? "none" : "1px solid #f0f4f8",
        padding:"8px 12px",
        display:"flex",flexDirection:"column",justifyContent:"center",
      }}>
        <div style={{fontFamily:DS.fontDisplay,fontSize:13,fontWeight:600,color:DS.marineDark,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {logement.nom}
        </div>
        <div style={{fontSize:10,color:DS.textMuted,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis"}}>
          {logement.prop}
        </div>
      </div>

      {/* PR #167c amend C18 — Col 1 sticky sub-row 2 MIN NUITS :
          label "Nb nuits min" en italique petit gris. Hauteur compacte 24px,
          aligné centre vertical. Sticky left identique à la col 1 prix.
          Affiché UNIQUEMENT si showMinStay=true. */}
      {showMinStay && (
        <div style={{
          gridColumn:1,
          gridRow: minStayGridRow,
          position:"sticky",left:0,zIndex:2,
          background:DS.white,borderRight:"1px solid #e8edf4",
          borderBottom:"1px solid #e8edf4", // séparateur fin de paire logement
          padding:"0 12px",
          display:"flex",alignItems:"center",justifyContent:"flex-start",
        }}>
          <span style={{
            fontSize:10,
            fontStyle:"italic",
            color:"#888",
            whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis",
          }}>
            Nb nuits min
          </span>
        </div>
      )}

      {/* Sub-row 1 (PRIX) : Cells jour vides + prix journalier discret.
          + Phase 6.B PR5A (D-7) : cellule bloquée fond gris + 🔒 + badge ⟳.
          Les barres résa se superposent ici par z-index. */}
      {monthDays.map((d, i) => {
        const we = isWeekend(d)
        const isToday = isSameDay(d, today)
        const dateISO = toISODate(d)
        const cellData = showPriceForLogement ? pricingForLogement?.get(dateISO) : null
        const cellPrice = cellData?.price1
        // LOT 5 — verrou manuel par date (badge 📌 punaise dans le coin —
        // distinct du 🔒 caution/bloc pour éviter toute confusion visuelle).
        const isLocked = cellData?.is_manual_override === true
        // LOT 5 — push outbox non confirmé couvrant cette date (badge ⚠️).
        const outboxStatus = outboxPendingForLogement?.get(dateISO) || null
        const hasPendingPush = !!outboxStatus
        // Phase 6.B PR5A — recherche block actif pour cette date.
        const block = findBlockForDate(blocksForLogement, dateISO)
        const isBlocked = !!block
        // PR #169a v1.4 (ADR-104) — distinction visuelle blocage simple vs
        // résa direct interne FBZ via helper getBlockVisualState.
        const blockVisual = getBlockVisualState(block)
        // PR #168 D-3 — la cellule est sous résa si une résa du logement couvre la date
        // (convention chambre d'hôtel start_date inclus → end_date exclus, déjà
        // appliquée par resaIdByLogementByDate côté parent). Bug "34" : sans guard,
        // le pricing s'affichait par-dessus la barre résa.
        const hasResa = !!resaIdByDate?.get(dateISO)
        // Décision Dimitri « un blocage est un blocage » : sur la VUE calendrier,
        // le tooltip d'un bloc simple n'expose plus la reason (« Blocage » seul).
        // La reason reste dans le détail (ResaV2Modal). Le direct garde son nom.
        const blockTooltip = isBlocked
          ? (blockVisual.isDirect
              ? `👤 Résa direct interne — (${block.start_date} → ${block.end_date}) · ${block.guest_first_name || ''} ${block.guest_last_name || ''}`.trimEnd()
              : `🔒 Blocage (${block.start_date} → ${block.end_date})${!block.beds24_pushed ? ' · ⟳ en attente Beds24' : ''}`)
          : undefined
        return (
          <div
            key={'c' + rowIdx + '-' + i}
            data-testid={`calendar-cell-${logement.id}-${dateISO}`}
            data-has-resa={hasResa ? 'true' : undefined}
            onClick={isClickable ? () => onCellClick(logement.id, dateISO, block) : undefined}
            title={blockTooltip}
            style={{
              gridColumn:i + 2, gridRow: priceGridRow,
              // v3-A LOT A (cosmétique) : le fond de cellule bloc N'EST PLUS peint
              // en carré bgColor. La couleur du bloc est portée UNIQUEMENT par la
              // barre continue ARRONDIE (blockBars, z-index 1) — exactement comme
              // une barre résa. La cellule garde le fond neutre today/weekend/
              // transparent → l'arrondi de la barre n'est plus masqué par un carré
              // de même couleur dans les coins (fin de l'aspect « carré, trop grand »).
              // Couleurs sémantiques (_block-visual.js) inchangées : elles restent
              // portées par la barre. Attributs testid/has-resa/title/onClick intacts.
              background: isToday ? "rgba(216, 90, 48, 0.05)" : (we ? "#f6f6f7" : "transparent"),
              borderRight:"1px solid #f0f4f8",
              // PR #167c amend C5 : pas de border-bottom si la sub-row min_stay
              // existe dessous (ligne fictive du logement). border vient sub-row 2.
              borderBottom: showMinStay ? "none" : "1px solid #f0f4f8",
              display:"flex",alignItems:"center",justifyContent:"center",
              position:"relative",
              cursor:isClickable ? "pointer" : "default",
            }}
          >
            {isBlocked ? (
              // BUG1 — l'icône (👤/🔒) + badge ⟳ per-cell sont retirés : ils sont
              // désormais portés UNE seule fois par la barre continue (blockBars,
              // couche de rendu parente). v3-A LOT A : le fond de cellule bgColor
              // est également retiré (la couleur du bloc vient de la barre arrondie
              // seule). On conserve ici le data-testid, data-has-resa, le onClick
              // cellule et le title bloc (contrats E2E pricing-blocks / pr169a-bis).
              // La cellule ne rend plus d'icône répétée → fin de l'illusion
              // « plusieurs voyageurs ».
              null
            ) : (
              // PR #168 D-3 — !hasResa guard : pas de prix par-dessus une barre résa.
              !hasResa && cellPrice != null && (
                <span
                  style={{
                    ...PRICING_TEXT_STYLE.tableau,
                    fontFamily:DS.fontBody,
                    // VERROU SÉLECTIF — repère LISIBLE SANS COULEUR : un prix
                    // fixé manuellement est SOULIGNÉ EN POINTILLÉ. Le pointillé
                    // dit « cette valeur ne suit pas Beds24 » par la FORME, pas
                    // par la teinte (jeton DS.marine déjà utilisé par la punaise
                    // de la même cellule — aucune valeur de couleur nouvelle).
                    ...(isLocked
                      ? { borderBottom:`1px dashed ${DS.marine}`, paddingBottom:1 }
                      : null),
                  }}
                  data-locked={isLocked ? "true" : undefined}
                  title={isLocked ? PRICING_LOCK_LABEL : undefined}
                >
                  {cellPrice}
                </span>
              )
            )}
            {/* LOT 5 — badges coin : 🔒 verrou manuel (haut-gauche) + ⚠️ push
                non confirmé (haut-droite). Discrets, position absolue, n'altèrent
                pas la couleur de la case. Masqués sous bloc / sous résa (la case
                est alors couverte par la barre continue z-index 1). */}
            {!isBlocked && !hasResa && isLocked && (
              <span
                data-testid={`pricing-lock-${logement.id}-${dateISO}`}
                title={PRICING_LOCK_LABEL}
                aria-label={PRICING_LOCK_LABEL}
                style={{
                  // VERROU SÉLECTIF — la punaise était noyée (10px, à même le
                  // fond de cellule) : Dimitri a verrouillé 69 dates sans jamais
                  // voir le repère. Elle est désormais posée sur une pastille
                  // blanche cerclée → contraste garanti sur fond week-end,
                  // aujourd'hui ou neutre, sans introduire de couleur nouvelle
                  // (jetons DS.white / DS.marine existants).
                  position:"absolute", top:1, left:1,
                  lineHeight:0, pointerEvents:"none",
                  background:DS.white,
                  border:`1px solid ${DS.marine}`,
                  borderRadius:3,
                  padding:1,
                  display:"inline-flex",
                }}
              ><Pin size={11} strokeWidth={2.4} color={DS.marine} /></span>
            )}
            {!isBlocked && !hasResa && hasPendingPush && (
              <span
                data-testid={`pricing-desync-${logement.id}-${dateISO}`}
                title={`Synchro Beds24 non confirmée (${outboxStatus}) — prix ou blocage de période, en attente`}
                aria-label="synchro Beds24 non confirmée"
                style={{
                  position:"absolute", top:1, right:1,
                  lineHeight:0, pointerEvents:"none",
                }}
              ><CloudOff size={10} strokeWidth={2.2} color={DS.warning} /></span>
            )}
          </div>
        )
      })}

      {/* Sub-row 2 (MIN NUITS) — rendue UNIQUEMENT si showMinStay=true.
          Hauteur compacte 24px (gridTemplateRows parent). Texte aligné centre.
          PR #167c amend C5 (D-8) — différencie override ⚙️ amber vs défaut gris.
          PR #167c amend C18 — chiffre seul (`1`, `2`...) dans la cellule, le
          titre "Nb nuits min" est porté par la col 1 sticky (cf. div ci-dessus). */}
      {showMinStay && monthDays.map((d, i) => {
        const we = isWeekend(d)
        const isToday = isSameDay(d, today)
        const dateISO = toISODate(d)
        const cellData = showPriceForLogement ? pricingForLogement?.get(dateISO) : null
        const cellMinStay = cellData?.min_stay
        const minStayLabel = formatMinStayCellShort({
          override: cellMinStay,
          defaultValue: minStayDefault,
          showMinStay: true,
        })
        const block = findBlockForDate(blocksForLogement, dateISO)
        const isBlocked = !!block
        // v3-A LOT A (cosmétique) : la sub-row min_stay ne peint plus le carré
        // bgColor du bloc (symétrie avec la sub-row prix) → le bloc ne déborde
        // plus vers le bas et ne dépasse plus la hauteur d'une barre résa.
        // getBlockVisualState n'est donc plus nécessaire ici (isBlocked suffit
        // pour masquer les chiffres min_stay sous le bloc).
        // PR #168 D-3 — guard symétrique sur sub-row min_stay (cohérence visuelle
        // avec sub-row prix : si la barre résa couvre la date, on n'affiche pas
        // les chiffres techniques par-dessus).
        const hasResa = !!resaIdByDate?.get(dateISO)
        return (
          <div
            key={'ms' + rowIdx + '-' + i}
            data-testid={`calendar-min-stay-cell-${logement.id}-${dateISO}`}
            data-has-resa={hasResa ? 'true' : undefined}
            style={{
              gridColumn:i + 2, gridRow: minStayGridRow,
              // v3-A LOT A (cosmétique) : plus de carré bgColor du bloc ici — fond
              // neutre today/weekend/transparent, comme sous une barre résa.
              background: isToday ? "rgba(216, 90, 48, 0.04)" : (we ? "#f6f6f7" : "transparent"),
              borderRight:"1px solid #f0f4f8",
              borderBottom:"1px solid #e8edf4", // séparateur entre logements
              display:"flex",alignItems:"center",justifyContent:"center",
              position:"relative",
              pointerEvents: "none", // sub-row info-only, clic remonte sur sub-row prix
            }}
          >
            {minStayLabel && !isBlocked && !hasResa && (
              <span style={{
                fontSize:12,
                color:minStayLabel.color,
                fontFamily:DS.fontBody,
                fontWeight: minStayLabel.badge ? 700 : 500,
                display:'flex',alignItems:'center',gap:1,
                lineHeight:1.1,
              }}
              aria-label={
                minStayLabel.badge
                  ? `Override actif sur défaut ${minStayDefault == null ? '' : `${minStayDefault} nuits`}`
                  : `Nb nuits min ${minStayLabel.text}`
              }
              >
                {minStayLabel.text}
                {minStayLabel.badge && (
                  <span style={{marginLeft:1,fontSize:9}}>{minStayLabel.badge}</span>
                )}
              </span>
            )}
          </div>
        )
      })}
    </>
  )
}, (prev, next) => {
  // BPRv3-4 : comparateur custom — pricingForLogement référence stable via cacheRef hook.
  // Si contenu inchangé (même signature batch), référence identique → re-render évité.
  return prev.logement === next.logement
    && prev.rowIdx === next.rowIdx
    && prev.N === next.N
    && prev.monthDays === next.monthDays
    && prev.today === next.today
    && prev.pricingForLogement === next.pricingForLogement
    && prev.modeForLogement === next.modeForLogement
    && prev.blocksForLogement === next.blocksForLogement
    && prev.resaIdByDate === next.resaIdByDate  // PR #168 D-3 — invariant via useMemo([filtered])
    && prev.outboxPendingForLogement === next.outboxPendingForLogement  // LOT 5
    && prev.onCellClick === next.onCellClick
    && prev.showMinStay === next.showMinStay   // PR #167b P-4 pessimist
    && prev.minStayDefault === next.minStayDefault  // PR #167c (MPRcv2-2)
    && prev.priceGridRow === next.priceGridRow  // PR #167c amend C5
})
